import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, Calendar, Users, Star, Heart, Wifi, Home as HomeIcon, UtensilsCrossed } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState('2');
  const [showSearchModal, setShowSearchModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeaturedProperties();
  }, []);

  const fetchFeaturedProperties = async () => {
    try {
      const response = await axios.get(`${API}/featured-properties`);
      setFeaturedProperties(response.data);
    } catch (error) {
      console.error('Error fetching featured properties:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/properties?city=${searchQuery}`);
  };

  return (
    <div data-testid="home-page" className="bg-white">
      {/* Hero Section with Airbnb-style Search */}
      <section className="relative" data-testid="hero-section">
        {/* Hero Image with Overlay */}
        <div className="relative h-[600px] bg-gradient-to-br from-blue-900 via-blue-800 to-gray-800">
          {/* Background Logo - Positioned Much Lower */}
          <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-0 opacity-25">
            <img 
              src="/logo.png" 
              alt="" 
              className="w-auto h-[300px] object-contain"
            />
          </div>
          
          <div className="relative z-10 max-w-6xl mx-auto px-4 py-20 text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-4" data-testid="hero-title">
              Find your next stay
            </h1>
            <p className="text-2xl mb-12" data-testid="hero-subtitle">
              Discover homes, apartments, and more
            </p>

            {/* Desktop Search Bar */}
            <div className="max-w-4xl mx-auto hidden md:block">
              <form onSubmit={handleSearch} className="bg-white rounded-full shadow-2xl p-2">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                  {/* Location */}
                  <div className="relative px-6 py-3">
                    <label className="text-xs font-semibold text-gray-900 block">Where</label>
                    <input
                      type="text"
                      placeholder="Search destinations"
                      className="text-sm text-gray-900 w-full outline-none border-none bg-transparent placeholder-gray-500"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      data-testid="search-input"
                    />
                  </div>

                  {/* Check-in */}
                  <div className="relative px-6 py-3 border-l border-gray-300">
                    <label className="text-xs font-semibold text-gray-900 block">Check in</label>
                    <input
                      type="text"
                      placeholder="Add dates"
                      className="text-sm text-gray-900 w-full outline-none border-none bg-transparent placeholder-gray-500"
                      value={checkIn}
                      onChange={(e) => setCheckIn(e.target.value)}
                      onFocus={(e) => e.target.type = 'date'}
                    />
                  </div>

                  {/* Check-out */}
                  <div className="relative px-6 py-3 border-l border-gray-300">
                    <label className="text-xs font-semibold text-gray-900 block">Check out</label>
                    <input
                      type="text"
                      placeholder="Add dates"
                      className="text-sm text-gray-900 w-full outline-none border-none bg-transparent placeholder-gray-500"
                      value={checkOut}
                      onChange={(e) => setCheckOut(e.target.value)}
                      onFocus={(e) => e.target.type = 'date'}
                    />
                  </div>

                  {/* Guests & Search Button */}
                  <div className="relative px-6 py-3 border-l border-gray-300 flex items-center justify-between">
                    <div className="flex-1">
                      <label className="text-xs font-semibold text-gray-900 block">Who</label>
                      <input
                        type="text"
                        placeholder="Add guests"
                        className="text-sm text-gray-900 w-full outline-none border-none bg-transparent placeholder-gray-500"
                        value={guests ? `${guests} guests` : ''}
                        onChange={(e) => setGuests(e.target.value.replace(/\D/g, ''))}
                      />
                    </div>
                    <button
                      type="submit"
                      className="bg-yellow-600 hover:bg-yellow-700 text-white p-4 rounded-full transition-colors ml-2"
                      data-testid="search-button"
                    >
                      <Search className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </form>
            </div>

            {/* Mobile Search Button - Transparent Glass Bar */}
            <div className="md:hidden max-w-xl mx-auto">
              <button
                onClick={() => setShowSearchModal(true)}
                className="w-full backdrop-blur-md bg-white/20 border border-white/30 rounded-full shadow-xl px-6 py-4 flex items-center gap-3 text-left hover:bg-white/30 transition-all"
              >
                <Search className="h-5 w-5 text-white" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white">Where to?</div>
                  <div className="text-xs text-white/70">Anywhere • Any week • Add guests</div>
                </div>
              </button>
            </div>

            {/* Mobile Search Modal */}
            {showSearchModal && (
              <div className="fixed inset-0 bg-white z-50 md:hidden overflow-y-auto">
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <button
                      onClick={() => setShowSearchModal(false)}
                      className="text-gray-600 hover:text-gray-900 text-2xl"
                    >
                      ✕
                    </button>
                    <h2 className="text-lg font-semibold text-gray-900">Search</h2>
                    <div className="w-6"></div>
                  </div>

                  {/* Search Form */}
                  <form onSubmit={(e) => { handleSearch(e); setShowSearchModal(false); }} className="space-y-4">
                    <div className="bg-white border-2 border-gray-300 rounded-xl p-4">
                      <label className="text-xs font-semibold text-gray-600 block mb-2">Where</label>
                      <input
                        type="text"
                        placeholder="Search destinations"
                        className="text-base text-gray-900 w-full outline-none border-none bg-transparent placeholder-gray-400"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                      />
                    </div>

                    <div className="bg-white border-2 border-gray-300 rounded-xl p-4">
                      <label className="text-xs font-semibold text-gray-600 block mb-2">Check in</label>
                      <input
                        type="date"
                        className="text-base text-gray-900 w-full outline-none border-none bg-transparent"
                        value={checkIn}
                        onChange={(e) => setCheckIn(e.target.value)}
                      />
                    </div>

                    <div className="bg-white border-2 border-gray-300 rounded-xl p-4">
                      <label className="text-xs font-semibold text-gray-600 block mb-2">Check out</label>
                      <input
                        type="date"
                        className="text-base text-gray-900 w-full outline-none border-none bg-transparent"
                        value={checkOut}
                        onChange={(e) => setCheckOut(e.target.value)}
                      />
                    </div>

                    <div className="bg-white border-2 border-gray-300 rounded-xl p-4">
                      <label className="text-xs font-semibold text-gray-600 block mb-2">Guests</label>
                      <input
                        type="number"
                        min="1"
                        max="8"
                        placeholder="Add guests"
                        className="text-base text-gray-900 w-full outline-none border-none bg-transparent"
                        value={guests}
                        onChange={(e) => setGuests(e.target.value)}
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-4 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
                    >
                      <Search className="h-5 w-5" />
                      Search
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Inspiration Section */}
      <section className="py-16 px-4 max-w-7xl mx-auto">
        <h2 className="text-3xl font-semibold text-gray-900 mb-8">Inspiration for your next trip</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <Link to="/properties?city=Sacramento" className="group">
            <div className="aspect-square rounded-xl overflow-hidden mb-3">
              <img 
                src="https://images.unsplash.com/photo-1598228723793-52759bba239c" 
                alt="Sacramento"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="font-semibold text-gray-900">Sacramento</h3>
            <p className="text-sm text-gray-600">Vacation rentals</p>
          </Link>

          <Link to="/properties?city=Los Angeles" className="group">
            <div className="aspect-square rounded-xl overflow-hidden mb-3">
              <img 
                src="https://images.unsplash.com/photo-1641910177671-4c75efb5383c" 
                alt="Los Angeles"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="font-semibold text-gray-900">Los Angeles</h3>
            <p className="text-sm text-gray-600">Apartment rentals</p>
          </Link>

          <Link to="/properties?state=Arizona" className="group">
            <div className="aspect-square rounded-xl overflow-hidden mb-3">
              <img 
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9" 
                alt="Arizona"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="font-semibold text-gray-900">Arizona</h3>
            <p className="text-sm text-gray-600">Beach house rentals</p>
          </Link>

          <Link to="/properties?city=San Bernardino" className="group">
            <div className="aspect-square rounded-xl overflow-hidden mb-3">
              <img 
                src="https://images.unsplash.com/photo-1580587771525-78b9dba3b914" 
                alt="San Bernardino"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <h3 className="font-semibold text-gray-900">San Bernardino</h3>
            <p className="text-sm text-gray-600">Cabin rentals</p>
          </Link>
        </div>
      </section>

      {/* Featured Properties - Airbnb Style */}
      <section className="py-16 px-4 bg-gray-50" data-testid="featured-properties-section">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 mb-8">Featured stays</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {featuredProperties.map((property) => (
              <Link
                key={property.id}
                to={`/property/${property.id}`}
                className="group"
                data-testid={`featured-property-${property.id}`}
              >
                <div className="relative mb-3">
                  <div className="aspect-square rounded-xl overflow-hidden">
                    <img
                      src={property.images[0]}
                      alt={property.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <button className="absolute top-3 right-3 p-2 rounded-full bg-white/80 hover:bg-white hover:scale-110 transition-all">
                    <Heart className="h-4 w-4 text-gray-700" />
                  </button>
                  {property.featured && (
                    <div className="absolute top-3 left-3 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Guest favorite
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-semibold text-gray-900 group-hover:underline">{property.city}, {property.state}</h3>
                    <div className="flex items-center gap-1">
                      <Star className="h-4 w-4 fill-yellow-600 text-yellow-600" />
                      <span className="text-sm font-semibold">4.9</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-1">{property.bedrooms} bedrooms · {property.bathrooms} baths</p>
                  <p className="text-sm text-gray-600 mb-2">Available now</p>
                  <div>
                    <span className="font-semibold text-gray-900">${property.price}</span>
                    <span className="text-gray-600"> night</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/properties"
              className="inline-block bg-blue-900 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-semibold transition-colors"
              data-testid="view-all-properties-link"
            >
              Show all properties
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us - Airbnb Style */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-semibold text-gray-900 mb-12">Why guests love staying with us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div>
              <div className="mb-4">
                <HomeIcon className="h-10 w-10 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Entire homes</h3>
              <p className="text-gray-600">
                Comfortable private places with everything you need for a great stay.
              </p>
            </div>

            <div>
              <div className="mb-4">
                <Wifi className="h-10 w-10 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Modern amenities</h3>
              <p className="text-gray-600">
                High-speed WiFi, smart TVs, and fully equipped kitchens in every rental.
              </p>
            </div>

            <div>
              <div className="mb-4">
                <UtensilsCrossed className="h-10 w-10 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Prime locations</h3>
              <p className="text-gray-600">
                Close to attractions, restaurants, and everything your destination offers.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;